import apiClient from './client';

const MEDIA_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.rocketgroup.io';

export interface MediaItem {
    id: number;
    user_id: number;
    original_filename: string;
    filename: string;
    filepath: string;
    thumbnail_path: string | null;
    media_type: 'image' | 'video' | 'audio' | 'icon';
    mime_type: string;
    file_size: number;
    width: number | null;
    height: number | null;
    is_active: number;
    created_at: string;
    updated_at: string;
}

// Construir URL completa a partir do filepath
export function getMediaUrl(filepath: string): string {
    return `${MEDIA_BASE_URL}/uploads/media/${filepath}`;
}

export function getThumbnailUrl(item: MediaItem): string {
    return getMediaUrl(item.thumbnail_path || item.filepath);
}

// Listar todas as mídias
export async function getMedia(mediaType?: string): Promise<MediaItem[]> {
    const response = await apiClient.get('/media', {
        params: mediaType ? { media_type: mediaType } : undefined,
    });
    return response.data;
}

interface CompressOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
}

// Redimensiona/comprime imagem no browser antes do upload.
// Pula SVG (vetor) e GIF (animação). Se o resultado ficar maior, devolve o original.
async function compressImage(file: File, options: CompressOptions = {}): Promise<File> {
    const { maxWidth = 1920, maxHeight = 1920, quality = 0.85 } = options;

    if (!file.type.startsWith('image/')) return file;
    if (file.type === 'image/svg+xml' || file.type === 'image/gif') return file;

    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        return file;
    }

    let width = bitmap.width;
    let height = bitmap.height;
    const ratio = Math.min(maxWidth / width, maxHeight / height, 1);

    // Já está dentro dos limites e é pequeno — nada a fazer
    if (ratio === 1 && file.size < 1024 * 1024) {
        bitmap.close?.();
        return file;
    }

    width = Math.round(width * ratio);
    height = Math.round(height * ratio);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap.close?.();
        return file;
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const hasAlpha = file.type === 'image/png' || file.type === 'image/webp';
    const outputType = hasAlpha ? 'image/png' : 'image/jpeg';
    const outputExt = hasAlpha ? 'png' : 'jpg';

    const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, outputType, quality);
    });

    if (!blob || blob.size >= file.size) return file;

    const newName = file.name.replace(/\.[^/.]+$/, '') + '.' + outputExt;
    return new File([blob], newName, { type: outputType, lastModified: Date.now() });
}

// Upload de mídia
export async function uploadMedia(file: File, isIcon: boolean = false): Promise<MediaItem> {
    const processed = await compressImage(
        file,
        isIcon ? { maxWidth: 256, maxHeight: 256, quality: 0.9 } : undefined,
    );
    const formData = new FormData();
    formData.append('file', processed);
    if (isIcon) {
        formData.append('is_icon', 'true');
    }
    const response = await apiClient.post('/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
}

// Deletar mídia
export async function deleteMedia(mediaId: number): Promise<{ message: string }> {
    const response = await apiClient.delete(`/media/${mediaId}`);
    return response.data;
}